import { openWindowWithTabAuth } from "./tabAuthStorage";

export const ACTIVE_VIDEO_ROOMS_KEY = ["activeVideoRooms"];

/** Caminho interno de onde o agente saiu ao abrir a sala (PWA / mobile, mesma janela). */
export const VIDEO_APP_RETURN_KEY = "xpro.video.appReturnPath";

export const ACTIVE_VIDEO_LIVE_COLOR = "error.main";

export const ACTIVE_VIDEO_ICON_SX = {
  fontSize: 18,
  color: ACTIVE_VIDEO_LIVE_COLOR,
};

function matchMedia(query) {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  try {
    return window.matchMedia(query).matches;
  } catch {
    return false;
  }
}

/**
 * PWA instalado (iOS / Android / desktop). Desktop também reporta standalone —
 * a decisão de ficar na mesma janela usa o tamanho da tela em separado.
 */
export function isInstalledPwaDisplay() {
  if (typeof window === "undefined") return false;
  return (
    matchMedia("(display-mode: standalone)") ||
    matchMedia("(display-mode: fullscreen)") ||
    matchMedia("(display-mode: minimal-ui)") ||
    window.navigator?.standalone === true
  );
}

/**
 * No celular/tablet (e no PWA instalado em tela estreita) a sala deve abrir na mesma
 * janela. window.open tira o usuário do app instalado e não há como voltar sem fechar tudo.
 * PWA de desktop continua em nova janela.
 */
export function shouldOpenAgentVideoInApp() {
  if (typeof window === "undefined") return false;
  const handheld =
    matchMedia("(max-width: 1024px)") && matchMedia("(pointer: coarse)");
  if (handheld) return true;
  if (!isInstalledPwaDisplay()) return false;
  return matchMedia("(max-width: 1100px)");
}

function sanitizeAppReturnPath(raw) {
  const value = String(raw || "").trim();
  if (!value.startsWith("/") || value.startsWith("//") || value.startsWith("/\\")) return null;
  if (value.includes("://") || value.includes("\\")) return null;
  if (value.startsWith("/video")) return null;
  return value.slice(0, 2048);
}

/** Guarda a rota atual do app para o botão Voltar da sala. Ignora hops entre salas. */
export function rememberVideoAppReturnPath() {
  if (typeof window === "undefined") return null;
  try {
    const existing = sanitizeAppReturnPath(sessionStorage.getItem(VIDEO_APP_RETURN_KEY));
    const currentPath = String(window.location?.pathname || "");
    if (currentPath.startsWith("/video") && existing) return existing;
    const next = sanitizeAppReturnPath(
      `${window.location?.pathname || "/"}${window.location?.search || ""}${window.location?.hash || ""}`
    );
    if (!next) return existing;
    sessionStorage.setItem(VIDEO_APP_RETURN_KEY, next);
    return next;
  } catch {
    return null;
  }
}

export function readVideoAppReturnPath() {
  if (typeof window === "undefined") return null;
  try {
    return sanitizeAppReturnPath(sessionStorage.getItem(VIDEO_APP_RETURN_KEY));
  } catch {
    return null;
  }
}

/**
 * Volta para a tela do app de onde a sala foi aberta.
 * @returns {boolean} true quando a navegação foi disparada
 */
export function returnFromAgentVideoToApp() {
  if (typeof window === "undefined") return false;
  const path = readVideoAppReturnPath();
  if (!path) return false;
  try {
    sessionStorage.removeItem(VIDEO_APP_RETURN_KEY);
  } catch {
    /* ignore */
  }
  try {
    window.location.assign(path);
    return true;
  } catch {
    return false;
  }
}

/**
 * Abre a chamada como agente clonando a sessão desta aba (evita JWT de outro workspace
 * herdado via localStorage na nova aba).
 * No mobile/PWA navega na mesma janela para o gesto e o botão Voltar retornarem ao app.
 * @returns {Window|null}
 */
export function openAgentVideoJoinUrl(joinUrl, { context, name, voRoomId, onBlocked } = {}) {
  if (!joinUrl) return null;
  let openUrl = String(joinUrl);
  try {
    const u = new URL(openUrl, typeof window !== "undefined" ? window.location.origin : undefined);
    u.searchParams.set("role", "agent");
    if (context) u.searchParams.set("context", String(context));
    const agentName = String(name || "").trim().slice(0, 80);
    if (agentName) u.searchParams.set("name", agentName);
    const voId = Number(voRoomId);
    if (Number.isFinite(voId) && voId > 0) {
      u.searchParams.set("voRoomId", String(voId));
    }
    openUrl = u.toString();
  } catch {
    /* keep openUrl */
  }
  if (shouldOpenAgentVideoInApp()) {
    rememberVideoAppReturnPath();
    try {
      window.location.assign(openUrl);
      return null;
    } catch {
      /* nova janela se a navegação interna falhar */
    }
  }
  const win = openWindowWithTabAuth(openUrl, "_blank");
  if (!win && typeof onBlocked === "function") {
    try {
      onBlocked();
    } catch {
      /* ignore */
    }
  }
  return win;
}

export function parseManualAgendaEventId(eventId) {
  const s = String(eventId || "");
  const m = s.match(/^manual:(\d+)$/);
  return m ? Number(m[1]) : null;
}

export function activeVideoTooltipKey({ isPrivate, canJoin }) {
  if (isPrivate && canJoin) return "activeVideo.tooltipPrivateJoin";
  if (isPrivate) return "activeVideo.tooltipPrivate";
  if (canJoin) return "activeVideo.tooltipJoin";
  return "activeVideo.tooltipPrivate";
}
